import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Specialite } from 'src/app/shared/models/specialite';
import { LogService } from 'src/app/core/services/log/log.service';
import { FacadeService } from 'src/app/core/services/facade.service';
import { Employee } from 'src/app/shared/models/employee/employee';
import { Unavailability } from 'src/app/shared/models/unavailability';
import {
	AbstractControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators
} from '@angular/forms';
import { Address } from 'src/app/shared/models/address';
import { Observable, of, Subscriber } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { PARAM_LABEL_INTERVENANT } from 'src/app/core/services/constant/constants.service';
import { User } from 'src/app/shared/models/security/user';
import { CareSkill } from 'src/app/shared/models/employee/care-skill';
import { EmployeeType } from 'src/app/shared/models/employee/employee-type';
import { NominatimResponse } from 'src/app/shared/models/api-response/nominatim-response';
import { Area } from 'src/app/shared/models/area';
import { CenterType } from 'src/app/shared/enums/center-type.enum';
import { CenterDepartment } from 'src/app/shared/models/center-department';
import { ConvertObjectToCalendarEvent } from 'src/app/shared/utils/convert-object-to-calendar-event';

@Component({
	selector: 'app-employee-form',
	templateUrl: './employee-form.component.html',
	styleUrls: ['./employee-form.component.scss']
})
export class EmployeeFormComponent implements OnInit {
	@Input() employee: Employee;
	@Input() isAddForm: boolean;
	@Input() isAddressSelected: { value: boolean };
	@Input() isWorkplaceAddressSelected: { value: boolean };
	@Input() isAddressDefault: { value: boolean };
	@Input() isWorkplaceAddressDefault: { value: boolean };
	//Boolean empêchant l'utilisateur d'envoyer plusieurs fois le formulaire
	@Input() isValidateButtonClicked: { value: boolean };
	@Input() form: FormGroup;
	@Output() sendForm: EventEmitter<any> = new EventEmitter();
	@Output() hideModal: EventEmitter<any> = new EventEmitter();

	MAX_NAME_CHAR = 25;
	NAME_PATTERN = new RegExp("^[a-zA-z'éèàç.-_]+( [a-zA-z'éèàç.-_]+)*$");

	employees: Employee[] = null;

	labelIntervenantCapitalized: string;

	weekDays = [
		'LUNDI',
		'MARDI',
		'MERCREDI',
		'JEUDI',
		'VENDREDI',
		'SAMEDI',
		'DIMANCHE'
	];

	selectedaddress?: Address;
	selectedworkplaceaddress?: Address;
	typeaheadLoading?: boolean;
	addressList: Observable<NominatimResponse[]>;
	workplaceAddressList: Observable<NominatimResponse[]>;
	searchResults: NominatimResponse[];

	listSpecialite: Specialite[];
	listSelectedSpecialite: Specialite[];

	competences;
	selectedCompetences = [];

	isPreferencesActive = false;

	unavailabilitiesMapped: {
		id: number;
		startDate: Date;
		endDate: Date;
	}[] = [];

	newUnavailability: Unavailability = new Unavailability();
	newUnavailabilityMapped: { id: number; startDate: Date; endDate: Date } = {
		id: undefined,
		startDate: undefined,
		endDate: undefined
	};
	isUnavaibilityTimeValid = true;

	user: User;

	emailActive = false;

	isSESSAD = false;
	isSAAD = false;
	isSSIAD = false;
	isSAE = false;

	isAreaActive = false;
	areaList: Area[] = [];
	centerDepartments: CenterDepartment[] = [];

	public EmployeeTypeEnum = EmployeeType;
	listSelectedArea: any = [];
	listSelectedDepartment: any = [];

	colorsPastel;

	constructor(
		private logger: LogService,
		private facade: FacadeService,
		private convertObjectToCalendarEvent: ConvertObjectToCalendarEvent
	) {
		this.addressList = new Observable((observer: Subscriber<string>) => {
			// Runs on every search
			observer.next(this.form.value.address);
		}).pipe(
			mergeMap((token: string) =>
				this.getNominatimDisplaynameAsObservable(token, false)
			)
		);

		this.workplaceAddressList = new Observable(
			(observer: Subscriber<string>) => {
				// Runs on every search
				observer.next(this.form.value.workplaceAddress);
			}
		).pipe(
			mergeMap((token: string) =>
				this.getNominatimDisplaynameAsObservable(token, true)
			)
		);
	}

	ngOnInit(): void {
		this.colorsPastel = this.convertObjectToCalendarEvent.colorsPastel;
		// this.sortColorsByHue();

		if (this.employee) {
			this.isAreaActive = this.facade.dataStorageService.isAreaActive();

			this.labelIntervenantCapitalized =
				this.facade.dataStorageService.getLabelCapitalized(
					PARAM_LABEL_INTERVENANT
				);

			if (this.isAreaActive) {
				this.facade.areaService.getAllAreasForUserCenter().subscribe({
					next: (areas: Area[]) => {
						this.areaList = [...areas];
					},
					error: (err) => {
						this.logger.error(err);
					}
				});
			}

			this.facade.centerDepartmentService
				.getAllCenterDepartmentsForUserCenter()
				.subscribe({
					next: (centerDepartments: CenterDepartment[]) => {
						this.centerDepartments = [...centerDepartments];
					},
					error: (err) => {
						this.logger.error(err);
					}
				});

			this.facade.specialiteService.getAllSpecialites().subscribe({
				next: (val) => {
					this.listSpecialite = val;
				},
				error: (err) => {
					this.logger.error(err);
				}
			});

			this.user = this.facade.dataStorageService.getUser();

			this.isPreferencesActive =
				this.facade.dataStorageService.isPreferencesBeneficiaireActive();

			this.isSESSAD = this.facade.dataStorageService.isSESSAD();
			this.isSAAD = this.facade.dataStorageService.isSAAD();
			this.isSSIAD = this.facade.dataStorageService.isSSIAD();
			this.isSAE = this.facade.dataStorageService.isSAE();
			this.facade.careSkillService
				.getAllCareSkills()
				.subscribe((careskills) => (this.competences = careskills));

			// Deep copy
			this.listSelectedSpecialite = [...this.employee.specialties];
			this.selectedCompetences = [...this.employee.careSkills];
			if (this.employee.areas) {
				this.listSelectedArea = [...this.employee.areas];
			}
			if (
				this.employee.centerDepartments &&
				this.employee.centerDepartments.length > 0
			) {
				this.listSelectedDepartment = [...this.employee.centerDepartments];
			}

			this.orderUnavailabilities();
			this.mapUnavailabilities();

			if (!this.isAddForm) {
				if (this.employee.address && this.employee.address.label) {
					this.isAddressDefault.value = true;
				}
				this.form.setValue({
					firstName: this.employee.firstName,
					lastName: this.employee.lastName,
					address: this.employee.address.label,
					workplaceAddress: this.employee.workplaceAddress?.label,
					birthDate:
						this.employee.birthDate !== null && this.employee.birthDate !== ''
							? new Date(this.employee.birthDate)
							: '',
					phone: this.employee.phone,
					autoAssignment: this.employee.autoAssignment,
					workingHoursPerWeek: this.employee.workingHoursPerWeek,
					employeeType: this.employee.type,
					email: this.employee.email
				});

				this.emailActive = this.employee.type === 'EXT' ? false : true;
			} else {
				this.toggleEmployeeTypeSelected();
			}
			this.address.setValidators([
				Validators.required,
				Validators.maxLength(60),
				this.isAddressValidValidator(),
				this.isWorkplaceAddressValidValidator()
			]);
		}
	}

	orderUnavailabilities() {
		this.employee?.unavailabilities.sort((a, b) => {
			if (this.weekDays.indexOf(a.day) < this.weekDays.indexOf(b.day)) {
				return -1;
			}
			if (this.weekDays.indexOf(a.day) > this.weekDays.indexOf(b.day)) {
				return 1;
			}
			return 0;
		});
	}

	mapUnavailabilities() {
		this.orderUnavailabilities();
		this.unavailabilitiesMapped = [];
		this.employee?.unavailabilities.map((unavailability: Unavailability) => {
			this.unavailabilitiesMapped.push({
				id: unavailability.id,
				startDate: new Date(
					new Date(new Date().setHours(0, 0, 0)).setMinutes(
						unavailability.start
					)
				),
				endDate: new Date(
					new Date(new Date().setHours(0, 0, 0)).setMinutes(unavailability.end)
				)
			});
		});
	}

	// ngOnChanges(): void {
	// 	// this.mapUnavailabilities();
	// }

	onAddressSelect(event: TypeaheadMatch, isWorkPlaceAddress: boolean) {
		if (!isWorkPlaceAddress) {
			this.isAddressDefault.value = false;
			this.isAddressSelected.value = true;
			this.selectedaddress = new Address();
			this.selectedaddress.label = event.item.displayName;
			this.selectedaddress.latitude = event.item.latitude;
			this.selectedaddress.longitude = event.item.longitude;
			this.form.controls['address'].updateValueAndValidity();
		} else {
			this.isWorkplaceAddressDefault.value = false;
			this.isWorkplaceAddressSelected.value = true;
			this.selectedworkplaceaddress = new Address();
			this.selectedworkplaceaddress.label = event.item.displayName;
			this.selectedworkplaceaddress.latitude = event.item.latitude;
			this.selectedworkplaceaddress.longitude = event.item.longitude;
			this.form.controls['workplaceAddress'].updateValueAndValidity();
		}
	}

	getNominatimDisplaynameAsObservable(
		token: string,
		isWorkplaceAddress: boolean
	): Observable<NominatimResponse[]> {
		if (!isWorkplaceAddress) {
			this.isAddressSelected.value = false;
		} else {
			this.isWorkplaceAddressSelected.value = false;
		}
		this.facade.nominatimService.addressLookup(token).subscribe({
			next: (result: any) => {
				this.searchResults = result.features.map(
					(feature) =>
						new NominatimResponse(
							feature.geometry.coordinates[0],
							feature.geometry.coordinates[1],
							feature.properties.label
						)
				);
			},
			error: (err) => {
				this.logger.error(err);
			}
		});
		return of(this.searchResults);
	}

	selectedAddressKeyUp(e: any, isWorkPlaceAddress: boolean) {
		if (!isWorkPlaceAddress) {
			this.isAddressDefault.value = false;
			if (this.selectedaddress) {
				if (this.selectedaddress?.label === e.value) {
					this.isAddressSelected.value = true;
				}
			}
			this.form.controls['address'].updateValueAndValidity();
		} else {
			this.isWorkplaceAddressDefault.value = false;
			if (this.selectedworkplaceaddress) {
				if (this.selectedworkplaceaddress?.label === e.value) {
					this.isWorkplaceAddressSelected.value = true;
				}
			}
			this.form.controls['workplaceAddress'].updateValueAndValidity();
		}
	}

	changeTypeaheadLoading(e: boolean): void {
		this.typeaheadLoading = e;
	}

	isSpecialiteCheck(spec: Specialite): boolean {
		return this.listSelectedSpecialite?.some(
			(specItfc) => specItfc.id === spec.id
		);
	}

	isCompetenceCheck(comp: CareSkill): boolean {
		return this.selectedCompetences?.some(
			(currComp) => currComp.id === comp.id
		);
	}

	isAreaCheck(area: Area): boolean {
		return this.listSelectedArea?.some((areaItfc) => areaItfc.id === area.id);
	}

	isDepartmentCheck(department: CenterDepartment): boolean {
		return this.listSelectedDepartment?.some((dpt) => dpt.id === department.id);
	}

	/**
	 * Ajoute ou supprime le secteur à la liste des secteurs sélectionnées
	 * @param e Element html
	 * @param area String representant le secteur choisi
	 */
	updateSelectedArea(e: any, area: Area) {
		if (e.target.checked) {
			if (!this.listSelectedArea.some((z) => z.id === area.id)) {
				this.listSelectedArea.push(area);
			}
		} else {
			if (this.listSelectedArea.some((z) => z.id === area.id)) {
				this.listSelectedArea = this.listSelectedArea.filter(
					(z) => z.id !== area.id
				);
			}
		}
	}

	updateSelectedDepartment(e: any, department: CenterDepartment) {
		if (e.target.checked) {
			if (!this.listSelectedDepartment.some((z) => z.id === department.id)) {
				this.listSelectedDepartment.push(department);
			}
		} else {
			if (this.listSelectedDepartment.some((z) => z.id === department.id)) {
				this.listSelectedDepartment = this.listSelectedDepartment.filter(
					(z) => z.id !== department.id
				);
			}
		}
	}

	/**
	 * Ajoute ou supprime la spécialité à la liste des spécialités sélectionnées
	 * @param e Element html
	 * @param spe String representant la specialite choisie
	 */
	updateSelectedSpecialite(e: any, spe: Specialite) {
		if (e.target.checked) {
			if (!this.listSelectedSpecialite.some((z) => z.id === spe.id)) {
				this.listSelectedSpecialite.push(spe);
			}
		} else {
			if (this.listSelectedSpecialite.some((z) => z.id === spe.id)) {
				this.listSelectedSpecialite = this.listSelectedSpecialite.filter(
					(z) => z.id !== spe.id
				);
			}
		}
	}

	updateSelectedCompetence(e: any, comp: CareSkill) {
		if (e.target.checked) {
			if (!this.selectedCompetences?.some((z) => z.id === comp.id)) {
				this.selectedCompetences.push(comp);
			}
		} else {
			if (this.selectedCompetences?.some((z) => z.id === comp.id)) {
				this.selectedCompetences = this.selectedCompetences.filter(
					(z) => z.id !== comp.id
				);
			}
		}
	}

	submitForm() {
		if (
			this.form.valid &&
			(this.isAddressSelected.value || this.isAddressDefault.value)
		) {
			this.isValidateButtonClicked.value = true;
			this.employee.firstName = this.form.get('firstName').value;
			this.employee.lastName = this.form.get('lastName').value;
			if (this.isAddressSelected.value && this.selectedaddress) {
				this.employee.address = this.selectedaddress;
			} else {
				if (this.isAddForm) {
					const adr = new Address();
					adr.label = this.form.get('address').value;
					this.employee.address = adr;
				}
			}
			if (
				this.isWorkplaceAddressSelected.value &&
				this.selectedworkplaceaddress
			) {
				this.employee.workplaceAddress = this.selectedworkplaceaddress;
			} else {
				if (this.isAddForm) {
					const adr = new Address();
					adr.label = this.form.get('workplaceAddress').value;
					this.employee.workplaceAddress = adr;
				}
			}
			if (!this.employee.careSkills) {
				this.employee.careSkills = [];
			}
			this.employee.careSkills = [...this.selectedCompetences];
			// this.selectedCompetences.forEach(c => this.employee.careSkills.push(c));

			if (this.listSelectedSpecialite.length >= 2) {
				this.listSelectedSpecialite = this.listSelectedSpecialite.filter(
					(e) => e.name !== 'Sans'
				);
			}
			this.employee.specialties = this.listSelectedSpecialite;
			this.employee.birthDate = this.form.get('birthDate').value;
			this.employee.phone = this.form.get('phone').value;
			this.employee.autoAssignment = this.form.get('autoAssignment').value;
			this.employee.workingHoursPerWeek = this.form.get(
				'workingHoursPerWeek'
			)?.value;

			this.employee.type = this.form.get('employeeType').value;
			if (this.form.get('employeeType').value === EmployeeType.EXT) {
				this.employee.autoAssignment = false;
			}
			this.employee.email = this.form.get('email').value;

			this.employee.areas = null;

			this.employee.centerDepartments = this.listSelectedDepartment;

			if (this.isAreaActive) {
				// if (this.form.value.area !== 'Non spécifié') {
				// 	this.employee.areas = [
				// 		this.areaList.find((area) => {
				// 			return area.name === this.form.value.area;
				// 		})
				// 	];
				// }
				this.employee.areas = this.listSelectedArea;
			}
			this.sendForm.emit();
		} else {
			// this.facade.toastService.createErrorToast();
			this.isValidateButtonClicked.value = false;
		}
	}

	toggleEmployeeTypeSelected() {
		const value = this.employeeType.value === this.EmployeeTypeEnum.EMP;
		this.emailActive = value;
		this.form.controls['autoAssignment'].setValue(value);
		if (!value) {
			this.form.controls['email'].setValue('');
			this.form.controls['workingHoursPerWeek'].setValue(35);
		}
	}

	setEmployeeColor(index: number): void {
		this.employee.colorIndex = index;
	}

	// Convertit une couleur HEX en HSL
	hexToHsl(hex: string): { h: number; s: number; l: number } {
		hex = hex.replace('#', '');
		const r = parseInt(hex.substring(0, 2), 16) / 255;
		const g = parseInt(hex.substring(2, 4), 16) / 255;
		const b = parseInt(hex.substring(4, 6), 16) / 255;

		const max = Math.max(r, g, b);
		const min = Math.min(r, g, b);
		let h = 0;
		let s = 0;
		const l = (max + min) / 2;

		if (max !== min) {
			const delta = max - min;
			s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

			switch (max) {
				case r:
					h = (g - b) / delta + (g < b ? 6 : 0);
					break;
				case g:
					h = (b - r) / delta + 2;
					break;
				case b:
					h = (r - g) / delta + 4;
					break;
			}
			h /= 6;
		}
		return { h: h * 360, s: s * 100, l: l * 100 };
	}

	// Trie ta liste colorsPastel par teinte (hue)
	sortColorsByHue(): void {
		this.colorsPastel.sort((a, b) => {
			const hslA = this.hexToHsl(a.secondary);
			const hslB = this.hexToHsl(b.secondary);
			return hslA.h - hslB.h;
		});
	}

	get firstName() {
		return this.form.get('firstName');
	}
	get lastName() {
		return this.form.get('lastName');
	}
	get address() {
		return this.form.get('address');
	}
	get workplaceAddress() {
		return this.form.get('workplaceAddress');
	}
	get competence() {
		return this.form.get('competence');
	}
	get phone() {
		return this.form.get('phone');
	}
	get workingHoursPerWeek() {
		return this.form.get('workingHoursPerWeek');
	}

	get area() {
		return this.form.get('area');
	}

	get department() {
		return this.form.get('department');
	}

	isAddressValidValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			return !(this.isAddressDefault.value || this.isAddressSelected.value)
				? { invalidAddress: { value: control.value } }
				: null;
		};
	}

	isWorkplaceAddressValidValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			return !(
				this.isWorkplaceAddressDefault.value ||
				this.isWorkplaceAddressSelected.value
			)
				? { invalidAddress: { value: control.value } }
				: null;
		};
	}

	get employeeType() {
		return this.form.get('employeeType');
	}
	get autoAssignment() {
		return this.form.get('autoAssignment');
	}
	get email() {
		return this.form.get('email');
	}

	get birthDate() {
		return this.form.get('birthDate');
	}
}
